import { type Quote, formatPrice } from "@tt/shared";

import { FlashValue } from "./FlashValue";
import { useEffect, useState } from "react";

import { api } from "../lib/api";

export function Watchlist({
  symbols, active, onPick, onRemove,
}: {
  symbols: string[];
  active: string;
  onPick: (s: string) => void;
  onRemove: (s: string) => void;
}) {
  const [quotes, setQuotes] = useState<Record<string, Quote>>({});
  const [unavailable, setUnavailable] = useState<string[]>([]);

  useEffect(() => {
    if (symbols.length === 0) return;
    let alive = true;
    const ac = new AbortController();
    const poll = async () => {
      try {
        const res = await api.quotes(symbols, ac.signal);
        if (alive) {
          setQuotes(res.quotes);
          setUnavailable(res.unavailable);
        }
      } catch {
        // A failed poll leaves the previous prices on screen, which is better
        // than blanking the list; the staleness is visible from the clock.
      }
    };
    void poll();
    const t = setInterval(poll, 15_000);
    return () => { alive = false; ac.abort(); clearInterval(t); };
  }, [symbols.join(",")]);

  return (
    <>
      <div className="wl-head">
        <span>Watchlist · {symbols.length}</span>
        {unavailable.length > 0 && (
          <span className="faint" title={`No quote for: ${unavailable.join(", ")}`}>
            {unavailable.length} n/a
          </span>
        )}
      </div>
      <div className="wl-list">
        {symbols.length === 0 && <div className="empty">Search a ticker to add it.</div>}
        {symbols.map((s) => {
          const q = quotes[s];
          const pct = q?.change_pct ?? null;
          const cls = pct === null ? "flat" : pct > 0 ? "up" : pct < 0 ? "down" : "flat";
          return (
            <div
              key={s}
              className={`wl-row ${s === active ? "active" : ""}`}
              onClick={() => onPick(s)}
            >
              <span className="sym">{s}</span>
              <FlashValue value={q?.price} className="px">
                {q ? formatPrice(q.price) : "—"}
              </FlashValue>
              <span className={`chg ${cls}`}>
                {pct === null ? "" : `${pct > 0 ? "+" : ""}${pct.toFixed(2)}%`}
              </span>
              <button
                className="wl-remove"
                title={`Remove ${s}`}
                onClick={(e) => { e.stopPropagation(); onRemove(s); }}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}
